import React from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge, LoadingSpinner, Table, TextLink, Tooltip } from '@/shared/components';
import { EyeIcon } from 'lucide-react';
import type { DealerConfigOption } from '../types/dealerConfigOption.types';

interface Props {
  data: DealerConfigOption[];
  isLoading?: boolean;
}

export const DealerConfigOptionDataTable: React.FC<Props> = ({ data, isLoading = false }) => {
  const navigate = useNavigate();

  if (isLoading) {
    return <LoadingSpinner size="xl" className="py-12" message="Loading config options..." />;
  }

  const columns: ColumnDef<DealerConfigOption>[] = [
    {
      accessorKey: 'key_name',
      header: 'Key Name',
      cell: (info) => <span className="font-mono text-sm">{info.getValue() as string}</span>,
    },
    {
      accessorKey: 'label',
      header: 'Label',
      cell: (info) => <span className="text-sm">{(info.getValue() as string | null) ?? '—'}</span>,
    },
    {
      accessorKey: 'data_type',
      header: 'Data Type',
      cell: (info) => <Badge size="xs" variant="info">{info.getValue() as string}</Badge>,
    },
    {
      accessorKey: 'group',
      header: 'Group',
      cell: (info) => {
        const group = info.getValue() as string | null;
        return group ? <Badge size="xs" variant="outline">{group}</Badge> : <span className="text-sm">—</span>;
      },
    },
    {
      accessorKey: 'is_required',
      header: 'Required',
      cell: (info) => <span className="text-sm">{(info.getValue() as boolean) ? 'Yes' : 'No'}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: (info) => (
        <div className="flex space-x-2">
          <Tooltip content="View config option">
            <TextLink
              onClick={() => navigate(`/admin/dealers/config-options/${info.row.original.id}`)}
              color="primary"
              startIcon={<EyeIcon size={20} />}
              ariaLabel="View config option"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <Table<DealerConfigOption>
      data={data}
      columns={columns}
      enableSorting={false}
      emptyMessage="No config options in the dealer catalog yet."
    />
  );
};
